import { isNativePlatform, isAndroid } from './platform';
import { logger } from '@utils/logger';

/**
 * Initialize Capacitor native plugins on app startup.
 *
 * Sets status bar style and enables the SafeArea plugin for proper
 * env(safe-area-inset-*) behavior on both iOS and Android.
 *
 * Must be called early in the app lifecycle (e.g., index.tsx).
 * No-ops on web/PWA since isNativePlatform() returns false.
 */
export async function initNativePlugins(): Promise<void> {
  if (!isNativePlatform()) return;

  // Import each plugin independently so one failure doesn't block the other
  let StatusBar: typeof import('@capacitor/status-bar').StatusBar | undefined;
  let Style: typeof import('@capacitor/status-bar').Style | undefined;

  try {
    const mod = await import('@capacitor/status-bar');
    StatusBar = mod.StatusBar;
    Style = mod.Style;
  } catch (e) {
    logger.error('Failed to import @capacitor/status-bar:', e);
  }

  let SafeArea: typeof import('@capacitor-community/safe-area').SafeArea | undefined;
  let SystemBarsStyle: typeof import('@capacitor-community/safe-area').SystemBarsStyle | undefined;

  try {
    const mod = await import('@capacitor-community/safe-area');
    SafeArea = mod.SafeArea;
    SystemBarsStyle = mod.SystemBarsStyle;
  } catch (e) {
    logger.error('Failed to import @capacitor-community/safe-area:', e);
  }

  // Set status bar to dark icons on light background (#F0EDEA)
  if (StatusBar && Style) {
    try {
      await StatusBar.setStyle({ style: Style.Light });
    } catch (e) {
      logger.warn('StatusBar.setStyle failed:', e);
    }

    // Android-only: set the status bar background color
    if (isAndroid()) {
      try {
        await StatusBar.setBackgroundColor({ color: '#F0EDEA' });
      } catch (e) {
        logger.warn('StatusBar.setBackgroundColor failed:', e);
      }
    }
  }

  // Configure system bar styles for the SafeArea plugin
  if (SafeArea && SystemBarsStyle) {
    try {
      await SafeArea.setSystemBarsStyle({ style: SystemBarsStyle.Light });
    } catch (e) {
      logger.warn('SafeArea.setSystemBarsStyle failed:', e);
    }
  }
}
